import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
// UPPDRAG-8 PR-B: dual-write helpers (see src/lib/chatDualWrite.ts header).
import { sendDirectMessage, markDirectMessagesRead } from "@/lib/chatDualWrite";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Send, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import type { Tables } from "@/integrations/supabase/types";
import { useTranslation } from "react-i18next";

type DirectMessageRow = Tables<"direct_messages">;

interface InlineDirectMessageProps {
  recipientId: string;
  hideHeader?: boolean;
}

export default function InlineDirectMessage({ recipientId, hideHeader }: InlineDirectMessageProps) {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const { organization } = useOrganization();
  const qc = useQueryClient();
  const [newMessage, setNewMessage] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: recipient } = useQuery({
    queryKey: ["dm-recipient", recipientId],
    queryFn: async () => {
      const { data } = await supabase
        .from("public_profiles" as any)
        .select("id, full_name, avatar_url, level")
        .eq("id", recipientId)
        .single();
      return data;
    },
    enabled: !!recipientId,
  });

  const { data: messages = [], isLoading } = useQuery({
    queryKey: ["direct-messages", user?.id, recipientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("direct_messages")
        .select("*")
        .or(
          `and(sender_id.eq.${user!.id},recipient_id.eq.${recipientId}),and(sender_id.eq.${recipientId},recipient_id.eq.${user!.id})`
        )
        .not("deleted_by", "cs", `{${user!.id}}`)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user?.id && !!recipientId,
  });

  // Realtime
  useEffect(() => {
    if (!user?.id || !recipientId) return;
    const channel = supabase
      .channel(`dm-inline-${user.id}-${recipientId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "direct_messages" },
        (payload) => {
          const msg = payload.new as DirectMessageRow;
          if (
            (msg.sender_id === user.id && msg.recipient_id === recipientId) ||
            (msg.sender_id === recipientId && msg.recipient_id === user.id)
          ) {
            qc.invalidateQueries({
              queryKey: ["direct-messages", user.id, recipientId],
            });
          }
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, recipientId, qc]);

  // Mark as read — UPPDRAG-8 PR-B: dual-write to legacy + new schema
  useEffect(() => {
    if (!user?.id || !recipientId || !organization?.id || messages.length === 0) return;
    const unread = messages.filter(
      (m: DirectMessageRow) => m.recipient_id === user.id && !m.read_at
    );
    if (unread.length > 0) {
      markDirectMessagesRead(supabase, {
        selfUserId: user.id,
        partnerUserId: recipientId,
        organizationId: organization.id,
        unreadMessageIds: unread.map((m: DirectMessageRow) => m.id),
      }).catch((err) => console.error("[InlineDirectMessage] markDirectMessagesRead failed:", err));
    }
  }, [messages, user?.id, recipientId, organization?.id]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // UPPDRAG-8 PR-B: dual-write to legacy direct_messages + new chat_messages.
  const sendMutation = useMutation({
    mutationFn: async () => {
      if (!organization?.id) throw new Error("No active organization");
      await sendDirectMessage(supabase, {
        senderUserId: user!.id,
        recipientUserId: recipientId,
        organizationId: organization.id,
        body: newMessage.trim(),
      });
    },
    onSuccess: () => {
      setNewMessage("");
      qc.invalidateQueries({
        queryKey: ["direct-messages", user?.id, recipientId],
      });
    },
  });

  const handleSend = () => {
    if (!newMessage.trim()) return;
    sendMutation.mutate();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header — hidden when embedded in unified inbox detail panel */}
      {!hideHeader && (
        <div className="flex items-center gap-3 p-4 border-b">
          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
            <span className="text-xs font-bold">
              {(recipient?.full_name?.split(" ")[0]?.[0] || "?").toUpperCase()}
            </span>
          </div>
          <span className="font-medium text-sm">
            {recipient?.full_name || "..."}
          </span>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2">
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-2/3" />
            ))}
          </div>
        ) : messages.length === 0 ? (
          <p className="text-center text-muted-foreground py-8 text-sm">
            {t("startConversation")}
          </p>
        ) : (
          messages.map((msg: DirectMessageRow) => {
            const isMe = msg.sender_id === user?.id;
            return (
              <div
                key={msg.id}
                className={cn("flex", isMe ? "justify-end" : "justify-start")}
              >
                <div
                  className={cn(
                    "max-w-[75%] rounded-2xl px-3 py-2 text-sm",
                    isMe
                      ? "bg-primary text-primary-foreground rounded-br-sm"
                      : "bg-muted rounded-bl-sm"
                  )}
                >
                  <p>{msg.message}</p>
                  <p
                    className={cn(
                      "text-[10px] mt-0.5",
                      isMe
                        ? "text-primary-foreground/60"
                        : "text-muted-foreground"
                    )}
                  >
                    {format(new Date(msg.created_at), "HH:mm")}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Input */}
      <div className="flex gap-2 p-4 border-t">
        <Input
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder={t("writeMessage")}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <Button
          size="icon"
          onClick={handleSend}
          disabled={!newMessage.trim() || sendMutation.isPending}
        >
          {sendMutation.isPending ? (
            <Loader2 className="animate-spin h-4 w-4" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
